import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';
import actionListaChamadosPorChat from 'src/actions/chamados/actionListaChamadosPorChat';
import actionPesquisarChamados from 'src/actions/chamados/actionPesquisarChamados';
import actionEditarChamado from 'src/actions/chamados/actionEditarChamado';
import { getVerChamado } from 'src/redux/slices/chamados';
import { fDateTime } from 'src/utils/formatTime';

const getStatusConfig = (t) => ({
    pendente: { label: t('chat.info.tickets.status.pendente'), className: 'bg-amber-50 text-amber-700 border-amber-200' },
    aberto: { label: t('chat.info.tickets.status.aberto'), className: 'bg-blue-50 text-blue-700 border-blue-200' },
    concluido: { label: t('chat.info.tickets.status.concluido'), className: 'bg-green-50 text-green-700 border-green-200' },
    recusado: { label: t('chat.info.tickets.status.recusado'), className: 'bg-red-50 text-red-700 border-red-200' },
    cancelado: { label: t('chat.info.tickets.status.cancelado'), className: 'bg-gray-50 text-gray-700 border-gray-200' },
});

ChatInfoChamados.propTypes = {
    chat: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    onOpenNovoChamado: PropTypes.func.isRequired,
    onCountUpdate: PropTypes.func,
    refreshTrigger: PropTypes.number,
};

export default function ChatInfoChamados({ chat, user, onOpenNovoChamado, onCountUpdate, refreshTrigger }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const statusConfig = getStatusConfig(t);
    const [chamados, setChamados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [vinculando, setVinculando] = useState(null);
    const debounceRef = useRef(null);

    const searchChamados = useCallback((termo) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!termo || termo.length < 2) {
            setSearchResults([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await actionPesquisarChamados({ cliente_id: user?.clienteId, search: termo, limit: 10 });
                const results = res.data?.data || res.data || [];
                const chamadoIds = chamados.map(c => c._id);
                setSearchResults(results.filter(r => !chamadoIds.includes(r._id)));
            } catch (error) {
                console.log('Erro ao pesquisar chamados', error);
            } finally {
                setSearchLoading(false);
            }
        }, 300);
    }, [user?.clienteId, chamados]);

    const handleVincularChamado = async (chamadoId) => {
        setVinculando(chamadoId);
        try {
            await actionEditarChamado(chamadoId, { action: "vincular_chat", chat_id: chat._id, usuarioId: user.id });
            setSearchResults(prev => prev.filter(r => r._id !== chamadoId));
            setSearchTerm('');
            setShowSearch(false);
            buscarChamados();
        } catch (error) {
            console.log('Erro ao vincular chamado', error);
        } finally {
            setVinculando(null);
        }
    };

    async function buscarChamados() {
        if (!chat?._id) return;
        setLoading(true);
        try {
            const response = await actionListaChamadosPorChat(chat._id);
            const data = response.data.data || [];
            setChamados(data);
            if (onCountUpdate) onCountUpdate(data.length);
        } catch (error) {
            console.log('Erro ao buscar chamados do chat', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        let cancelled = false;
        setChamados([]);
        setShowSearch(false);
        setSearchTerm('');
        setSearchResults([]);

        (async () => {
            if (!chat?._id) return;
            setLoading(true);
            try {
                const response = await actionListaChamadosPorChat(chat._id);
                const data = response.data?.data || [];
                if (cancelled) return;
                setChamados(data);
                if (onCountUpdate) onCountUpdate(data.length);
            } catch (error) {
                if (!cancelled) setChamados([]);
                console.log('Erro ao buscar chamados do chat', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [chat._id, refreshTrigger]);

    const getStatusBadge = (status) => {
        const config = statusConfig[status] || statusConfig.pendente;
        return (
            <Badge variant="outline" className={`text-[10px] font-medium ${config.className}`}>
                {config.label}
            </Badge>
        );
    };

    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <button
                    onClick={onOpenNovoChamado}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-[#7C3AED] bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
                >
                    <Iconify icon="eva:plus-outline" width={14} />
                    {t('common.buttons.create')}
                </button>
                <button
                    onClick={() => { setShowSearch(prev => !prev); setSearchTerm(''); setSearchResults([]); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${showSearch ? 'text-white bg-[#7C3AED] hover:bg-[#6d28d9]' : 'text-[#7C3AED] bg-violet-50 hover:bg-violet-100'}`}
                >
                    <Iconify icon="eva:link-2-outline" width={14} />
                    {t('chat.info.tickets.link')}
                </button>
            </div>

            {showSearch && (
                <div className="space-y-2 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="relative">
                        <Iconify icon="eva:search-outline" width={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('chat.info.tickets.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); searchChamados(e.target.value); }}
                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]/20"
                            autoFocus
                        />
                    </div>
                    {searchLoading && (
                        <p className="text-[10px] text-gray-400 text-center py-2">{t('chat.info.tickets.searching')}</p>
                    )}
                    {!searchLoading && searchTerm.length >= 2 && searchResults.length === 0 && (
                        <p className="text-[10px] text-gray-400 text-center py-2">{t('chat.info.tickets.noResults')}</p>
                    )}
                    {searchResults.length > 0 && (
                        <div className="max-h-[200px] overflow-y-auto space-y-1">
                            {searchResults.map((item) => (
                                <div
                                    key={item._id}
                                    onClick={() => !vinculando && handleVincularChamado(item._id)}
                                    className="p-2 rounded-md hover:bg-violet-50 dark:hover:bg-violet-900/20 cursor-pointer transition-colors flex items-center justify-between gap-2"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-mono text-gray-400">#{item.chamado}</span>
                                            {getStatusBadge(item.status)}
                                        </div>
                                        <p className="text-xs text-gray-700 dark:text-gray-300 truncate mt-0.5">
                                            {item.assunto || item.titulo || t('chat.info.tickets.noSubject')}
                                        </p>
                                    </div>
                                    {vinculando === item._id ? (
                                        <Iconify icon="svg-spinners:ring-resize" width={14} className="text-[#7C3AED] flex-shrink-0" />
                                    ) : (
                                        <Iconify icon="eva:link-2-outline" width={14} className="text-gray-400 flex-shrink-0" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {loading && chamados.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-xs text-gray-400">{t('common.buttons.loading')}</p>
                </div>
            )}

            {!loading && chamados.length === 0 && (
                <div className="text-center py-4">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-2">
                        <Iconify icon="eva:clipboard-outline" width={20} className="text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.tickets.empty')}</p>
                </div>
            )}

            {chamados.length > 0 && (
                <div className="space-y-1.5">
                    {chamados.map((chamado) => (
                        <div
                            key={chamado._id}
                            onClick={() => {
                                dispatch(getVerChamado(chamado._id));
                                navigate('/dashboard/chamados-chat');
                            }}
                            className="p-2.5 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg hover:border-violet-200 dark:hover:border-violet-600 hover:shadow-sm cursor-pointer transition-all"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-mono text-gray-400">#{chamado.chamado}</span>
                                        {getStatusBadge(chamado.status)}
                                    </div>
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mt-1 truncate">
                                        {chamado.assunto || chamado.titulo || t('chat.info.tickets.noSubject')}
                                    </p>
                                </div>
                            </div>
                            {chamado.data_criacao && (
                                <p className="text-[10px] text-gray-400 mt-1">{fDateTime(chamado.data_criacao)}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
